import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Modal,
    Input,
    DatePicker,
    Select,
    Space,
    Tag,
    Button,
    Row,
    Col,
    Typography,
    message,
    Statistic,
    Card,
} from 'antd';
import Table from 'components/Common/Table';
import {
    SearchOutlined,
    FileTextOutlined,
    ReloadOutlined,
    CheckCircleOutlined,
    UserOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';
import * as adminactions from "store/actions/AuthActions";
import { formatMoney, currencyPrefix, currencyDecimals, recordCurrency } from 'utils/money';

const { Text } = Typography;
const { RangePicker } = DatePicker;


// Invoice status colors and icons
const STATUS_CONFIG = {
    DRAFT: { color: 'default', icon: <FileTextOutlined />, label: 'Draft' },
    SENT: { color: 'blue', icon: <ClockCircleOutlined />, label: 'Sent' },
    VIEWED: { color: 'cyan', icon: <ClockCircleOutlined />, label: 'Viewed' },
    PARTIAL: { color: 'orange', icon: <ExclamationCircleOutlined />, label: 'Partial' },
    PAID: { color: 'green', icon: <CheckCircleOutlined />, label: 'Paid' },
    OVERDUE: { color: 'red', icon: <ExclamationCircleOutlined />, label: 'Overdue' },
    CANCELLED: { color: 'default', icon: <ExclamationCircleOutlined />, label: 'Cancelled' },
};

const InvoiceSelectModal = ({
    visible,
    onClose,
    onSelect,
    selectedInvoiceId,
    accountId = null,
    title = "Select Invoice",
    filterStatuses = null, // Array of statuses to filter, e.g., ['SENT', 'PARTIAL', 'OVERDUE']
}) => {
    const [loading, setLoading] = useState(false);
    const [invoices, setInvoices] = useState([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    // A ref, so the debounced search (created once) fetches with the current size.
    const pageSizeRef = useRef(10);
    const [searchInputValue, setSearchInputValue] = useState('');
    const [dateRange, setDateRange] = useState([null, null]);
    const [accounts, setAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(accountId);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [selectedRowKey, setSelectedRowKey] = useState(null);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [initialized, setInitialized] = useState(false);

    // Format an amount in the invoice's own currency (INR when it has none)
    const formatCurrency = (amount, record) => formatMoney(amount, recordCurrency(record));

    // Fetch accounts for filter dropdown
    const fetchAccounts = async () => {
        try {
            const response = await adminactions.loadAccounts(1, '', 1000);
            if (response && response.data) {
                setAccounts(response.data.results || response.data || []);
            }
        } catch (error) {
            console.error('Error fetching accounts:', error);
        }
    };

    // Fetch invoices
    const fetchInvoices = async (page = 1, search = '', dates = [null, null], account = null, status = null, pageSize = pageSizeRef.current) => {
        pageSizeRef.current = pageSize;
        setLoading(true);
        try {
            const response = await adminactions.loadInvoices(
                page,
                search,
                dates && dates[0] && dates[1] ? dates : null,
                account,
                status,
                pageSize
            );

            console.log('Invoice response:', response);

            if (response && response.data) {
                let results = response.data.results || [];

                console.log('Invoice results:', results, 'filterStatuses:', filterStatuses);

                // Client-side filter by statuses if provided and no specific status selected
                if (!status && filterStatuses && filterStatuses.length > 0) {
                    results = results.filter(inv => filterStatuses.includes(inv.status));
                }

                setInvoices(results);
                setPagination(prev => ({
                    ...prev,
                    current: page,
                    pageSize,
                    total: response.data.count || results.length,
                }));
            } else {
                console.log('No response data');
                setInvoices([]);
            }
        } catch (error) {
            console.error('Error fetching invoices:', error);
            message.error('Failed to fetch invoices');
            setInvoices([]);
        } finally {
            setLoading(false);
        }
    };

    // When modal opens, fetch data
    useEffect(() => {
        if (visible && !initialized) {
            setInitialized(true);
            fetchAccounts();
            setSelectedAccount(accountId);
            fetchInvoices(1, '', [null, null], accountId, null);
            setSelectedRowKey(selectedInvoiceId || null);
        }

        // Reset when modal closes
        if (!visible && initialized) {
            setInitialized(false);
            setSearchInputValue('');
            setDateRange([null, null]);
            setSelectedAccount(accountId);
            setSelectedStatus(null);
            setInvoices([]);
            setSelectedInvoice(null);
        }
    }, [visible]);

    // Debounced search
    const debouncedFetch = useMemo(
        () => debounce((search, dates, account, status) => {
            fetchInvoices(1, search, dates, account, status);
        }, 500),
        []
    );

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchInputValue(value);
        debouncedFetch(value, dateRange, selectedAccount, selectedStatus);
    };

    // Handle date range change
    const handleDateRangeChange = (dates) => {
        const newDates = dates || [null, null];
        setDateRange(newDates);
        fetchInvoices(1, searchInputValue, newDates, selectedAccount, selectedStatus);
    };

    // Handle account filter change
    const handleAccountChange = (value) => {
        setSelectedAccount(value || null);
        fetchInvoices(1, searchInputValue, dateRange, value || null, selectedStatus);
    };

    // Handle status filter change
    const handleStatusChange = (value) => {
        setSelectedStatus(value || null);
        fetchInvoices(1, searchInputValue, dateRange, selectedAccount, value || null);
    };

    const handleTableChange = (paginationConfig) => {
        const size = paginationConfig.pageSize;
        // Back to page 1 when the page size changes.
        const page = size !== pageSizeRef.current ? 1 : paginationConfig.current;
        fetchInvoices(page, searchInputValue, dateRange, selectedAccount, selectedStatus, size);
    };

    const handleReset = () => {
        setSearchInputValue('');
        setDateRange([null, null]);
        setSelectedAccount(accountId);
        setSelectedStatus(null);
        fetchInvoices(1, '', [null, null], accountId, null);
    };

    const handleRowSelect = (record) => {
        setSelectedRowKey(record.id);
        setSelectedInvoice(record);
    };

    const handleSelect = () => {
        const invoice = invoices.find(inv => inv.id === selectedRowKey);
        onSelect(selectedRowKey, invoice);
        onClose();
    };

    const getStatusTag = (status) => {
        const config = STATUS_CONFIG[status] || { color: 'default', label: status };
        return (
            <Tag color={config.color} icon={config.icon}>
                {config.label}
            </Tag>
        );
    };

    const columns = [
        {
            title: 'Invoice #',
            dataIndex: 'inv_number',
            key: 'inv_number',
            width: 130,
            render: (num) => (
                <Text strong style={{ color: '#1890ff' }}>{num}</Text>
            ),
        },
        {
            title: 'Account',
            dataIndex: 'account_data',
            key: 'account',
            width: 180,
            ellipsis: true,
            render: (account) => (
                <Space>
                    <UserOutlined style={{ color: '#8c8c8c' }} />
                    <Text>{account?.name || '-'}</Text>
                </Space>
            ),
        },
        {
            title: 'Date',
            dataIndex: 'inv_date',
            key: 'inv_date',
            width: 110,
            render: (date) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {date ? dayjs(date).format('DD/MM/YYYY') : '-'}
                </span>
            ),
        },
        {
            title: 'Due Date',
            dataIndex: 'due_date',
            key: 'due_date',
            width: 110,
            render: (date, record) => {
                const isOverdue = date && dayjs(date).isBefore(dayjs(), 'day') && record.status !== 'PAID';
                return (
                    <span style={{
                        whiteSpace: 'nowrap',
                        color: isOverdue ? '#f5222d' : 'inherit',
                        fontWeight: isOverdue ? 500 : 'normal',
                    }}>
                        {date ? dayjs(date).format('DD/MM/YYYY') : '-'}
                    </span>
                );
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) => getStatusTag(status),
        },
        {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            width: 130,
            align: 'right',
            render: (total, record) => (
                <Text strong style={{ whiteSpace: 'nowrap' }}>
                    {formatCurrency(total, record)}
                </Text>
            ),
        },
        {
            title: 'Balance',
            dataIndex: 'balance',
            key: 'balance',
            width: 130,
            align: 'right',
            render: (balance, record) => (
                <Text
                    strong
                    style={{
                        whiteSpace: 'nowrap',
                        color: balance > 0 ? '#fa8c16' : '#52c41a',
                    }}
                >
                    {formatCurrency(balance, record)}
                </Text>
            ),
        },
    ];

    const rowSelection = {
        type: 'radio',
        selectedRowKeys: selectedRowKey ? [selectedRowKey] : [],
        onChange: (selectedRowKeys, selectedRows) => {
            setSelectedRowKey(selectedRowKeys[0] || null);
            setSelectedInvoice(selectedRows[0] || null);
        },
    };

    // Status options for filter
    const statusOptions = [
        { value: 'DRAFT', label: 'Draft' },
        { value: 'SENT', label: 'Sent' },
        { value: 'VIEWED', label: 'Viewed' },
        { value: 'PARTIAL', label: 'Partial' },
        { value: 'PAID', label: 'Paid' },
        { value: 'OVERDUE', label: 'Overdue' },
        { value: 'CANCELLED', label: 'Cancelled' },
    ];

    return (
        <Modal
            title={
                <Space>
                    <FileTextOutlined style={{ color: '#1890ff' }} />
                    <span>{title}</span>
                </Space>
            }
            open={visible}
            onCancel={onClose}
            width={1200}
            destroyOnClose={false}
            footer={[
                <Button key="cancel" onClick={onClose}>
                    Cancel
                </Button>,
                <Button
                    key="clear"
                    onClick={() => {
                        setSelectedRowKey(null);
                        setSelectedInvoice(null);
                        onSelect(null, null);
                        onClose();
                    }}
                >
                    Clear Selection
                </Button>,
                <Button
                    key="select"
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={handleSelect}
                    disabled={!selectedRowKey}
                >
                    Select Invoice
                </Button>,
            ]}
        >
            {/* Filters */}
            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={12} md={6}>
                    <Input
                        placeholder="Search invoice #, account..."
                        prefix={<SearchOutlined />}
                        allowClear
                        value={searchInputValue}
                        onChange={handleSearchChange}
                    />
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <RangePicker
                        style={{ width: '100%' }}
                        placeholder={['Start Date', 'End Date']}
                        value={dateRange}
                        onChange={handleDateRangeChange}
                    />
                </Col>
                <Col xs={24} sm={12} md={5}>
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Select Account"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        value={selectedAccount}
                        onChange={handleAccountChange}
                        options={accounts.map(acc => ({
                            value: acc.id,
                            label: acc.name,
                        }))}
                    />
                </Col>
                <Col xs={24} sm={12} md={4}>
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Status"
                        allowClear
                        value={selectedStatus}
                        onChange={handleStatusChange}
                        options={statusOptions}
                    />
                </Col>
                <Col xs={24} sm={12} md={3}>
                    <Button icon={<ReloadOutlined />} onClick={handleReset} style={{ width: '100%' }}>
                        Reset
                    </Button>
                </Col>
            </Row>

            {/* Selected Invoice Preview */}
            {selectedInvoice && (
                <Card
                    size="small"
                    style={{
                        marginBottom: 16,
                        background: 'linear-gradient(135deg, #e6f7ff 0%, #f0f5ff 100%)',
                        border: '1px solid #91d5ff',
                    }}
                >
                    <Row gutter={24}>
                        <Col span={6}>
                            <Statistic
                                title="Selected Invoice"
                                value={selectedInvoice.inv_number}
                                prefix={<FileTextOutlined />}
                                valueStyle={{ color: '#1890ff', fontSize: 18 }}
                            />
                        </Col>
                        <Col span={6}>
                            <Statistic
                                title="Account"
                                value={selectedInvoice.account_data?.name || '-'}
                                valueStyle={{ fontSize: 16 }}
                            />
                        </Col>
                        <Col span={6}>
                            <Statistic
                                title="Total Amount"
                                value={selectedInvoice.total || 0}
                                prefix={currencyPrefix(recordCurrency(selectedInvoice))}
                                precision={currencyDecimals(recordCurrency(selectedInvoice))}
                                valueStyle={{ fontSize: 16 }}
                            />
                        </Col>
                        <Col span={6}>
                            <Statistic
                                title="Balance Due"
                                value={selectedInvoice.balance || 0}
                                prefix={currencyPrefix(recordCurrency(selectedInvoice))}
                                precision={currencyDecimals(recordCurrency(selectedInvoice))}
                                valueStyle={{
                                    fontSize: 16,
                                    color: selectedInvoice.balance > 0 ? '#fa8c16' : '#52c41a',
                                }}
                            />
                        </Col>
                    </Row>
                </Card>
            )}

            {/* Summary */}
            <div style={{ marginBottom: 12 }}>
                <Text type="secondary">
                    Showing {invoices.length} of {pagination.total} invoices
                    {selectedRowKey && (
                        <Tag color="blue" style={{ marginLeft: 8 }}>
                            1 selected
                        </Tag>
                    )}
                </Text>
            </div>

            {/* Table */}
            <Table
                rowSelection={rowSelection}
                columns={columns}
                dataSource={invoices}
                rowKey="id"
                loading={loading}
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: ['10', '20', '50'],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                }}
                onChange={handleTableChange}
                scroll={{ x: 900, y: 350 }}
                size="small"
                onRow={(record) => ({
                    onClick: () => handleRowSelect(record),
                    style: { cursor: 'pointer' },
                })}
                rowClassName={(record) =>
                    record.id === selectedRowKey ? 'ant-table-row-selected' : ''
                }
            />
        </Modal>
    );
};

export default InvoiceSelectModal;
